import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useClientPortalPaths } from "../../hooks/useClientPortalBase";
import { useVendorDirectoryPaths } from "../../hooks/useVendorDirectoryPaths";
import {
  getRetentionDashboard,
  previewRetentionRun,
  executeRetentionRun,
} from "../../api/clientVendorDirectoryPrivacyApi";
import ClientVendorDirectorySubnav from "../../components/casamap/admin/ClientVendorDirectorySubnav";
import { ClientPortalError, ClientPortalLoading } from "../../components/clientPortal/ClientPortalStates";
import { useLocale } from "../../intl";
import "../../styles/casamapaVendorDirectoryAdmin.css";

const COPY_PREFIX = "realEstate.casaMapa.admin.vendorDirectoryWorkspace.retentionPage";

export default function ClientVendorDirectoryRetentionDashboardPage() {
  const { clientViewId } = useParams();
  const { serviciosBase } = useVendorDirectoryPaths();
  const paths = useClientPortalPaths();
  const { t } = useLocale();
  const [dashboard, setDashboard] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const body = await getRetentionDashboard(clientViewId);
      setDashboard(body);
    } catch (error) {
      setErrorMessage(error.message || t(`${COPY_PREFIX}.loadError`));
    } finally {
      setLoading(false);
    }
  }, [clientViewId]);

  useEffect(() => {
    load();
  }, [load]);

  const runPreview = async () => {
    setBusy(true);
    try {
      const body = await previewRetentionRun(clientViewId, { dryRun: true });
      setPreview(body.preview);
    } catch (error) {
      window.alert(error.message);
    } finally {
      setBusy(false);
    }
  };

  const runExecute = async () => {
    if (!window.confirm("¿Ejecutar limpieza de retención? Esta acción puede eliminar archivos elegibles.")) return;
    setBusy(true);
    try {
      const body = await executeRetentionRun(clientViewId, { dryRun: false });
      setPreview(body.summary);
      await load();
    } catch (error) {
      window.alert(error.message);
    } finally {
      setBusy(false);
    }
  };

  const basePath = `${serviciosBase}/privacidad`;

  return (
    <div className="page-content cvd-admin">
      <ClientVendorDirectorySubnav clientViewId={clientViewId} />
      <Link className="btn" to={basePath}>
        ← Solicitudes de privacidad
      </Link>
      <h1>{t(`${COPY_PREFIX}.title`)}</h1>

      {loading ? <ClientPortalLoading message={t(`${COPY_PREFIX}.loading`)} /> : null}
      {errorMessage ? <ClientPortalError message={errorMessage} onRetry={load} /> : null}

      {!loading && dashboard ? (
        <>
          <div className="card cvd-section">
            <h2>Políticas activas</h2>
            <p>{dashboard.policies?.length || 0} políticas configuradas.</p>
            <ul>
              {(dashboard.policies || []).map((p) => (
                <li key={p.id}>
                  <strong>{p.policyKey}</strong> — {p.resourceType} — {p.retentionDurationDays ?? "indefinido"} días
                </li>
              ))}
            </ul>
          </div>
          <div className="cvd-risk-actions">
            <button type="button" className="btn" disabled={busy} onClick={runPreview}>
              Vista previa (dry-run)
            </button>
            <button type="button" className="btn btn-primary" disabled={busy} onClick={runExecute}>
              Ejecutar limpieza
            </button>
          </div>
          {preview ? (
            <div className="card cvd-section">
              <h2>Resultado</h2>
              <pre>{JSON.stringify(preview, null, 2)}</pre>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
